import type { TourConfig } from '../types/tour.types';

type TourLoader = () => Promise<{ default: TourConfig }>;

export const TOUR_REGISTRY: Record<string, TourLoader> = {
  // Exact matches
  '/dashboard': () => import('./tour-inicio'),
  '/cotiza/vision-general': () => import('./tour-dashboard'),
  '/quotes': () => import('./tour-quotes'),
  '/quotes/new': () => import('./tour-quote-create'),
  '/pricing-models': () => import('./tour-models'),
  '/approvals': () => import('./tour-approvals'),
  '/ai-intelligence': () => import('./tour-ai-intelligence'),
  '/master-data': () => import('./tour-master-data'),
  '/customers': () => import('./tour-customers'),
  // Usuarios vive en Mi empresa (`/users` redirige ahí).
  '/mi-empresa/usuarios': () => import('./tour-users'),
  '/materials': () => import('./tour-materials'),
  '/analytics': () => import('./tour-analytics'),
  '/ai-productivity': () => import('./tour-ai-productivity'),
  '/settings': () => import('./tour-settings'),
  '/profile': () => import('./tour-profile'),
  // Prefix matches (parameterized routes) - must end with /
  '/pricing-models/': () => import('./tour-model-detail'),
  '/quotes/': () => import('./tour-quote-detail'),
};

/**
 * Mini Prixia (X3): los recorridos de las seis misiones. Sólo los suma una empresa
 * demo (`registroDeTours(true)`); las demás siguen con `TOUR_REGISTRY` tal cual.
 * Productos › Vincular tiene clave propia (`?tab=vincular`, la entiende el
 * proveedor) para no compartir el recorrido de «Carga tu catálogo».
 */
export const TOUR_REGISTRY_DEMO: Record<string, TourLoader> = {
  '/compite/monitoreo': () => import('./tour-demo-monitoreo'),
  '/compite/productos': () => import('./tour-demo-productos'),
  '/compite/productos?tab=vincular': () => import('./tour-demo-vincular'),
  '/compite/analitica': () => import('./tour-demo-analitica'),
  '/compite/estrategia': () => import('./tour-demo-estrategia'),
  '/compite/decisiones': () => import('./tour-demo-decisiones'),
};

/** El registro que ve la sesión: el de siempre, más los de la demo si es una empresa demo. */
export function registroDeTours(esDemo: boolean): Record<string, TourLoader> {
  return esDemo ? { ...TOUR_REGISTRY, ...TOUR_REGISTRY_DEMO } : TOUR_REGISTRY;
}

export async function resolveTourConfig(
  route: string,
  registro: Record<string, TourLoader> = TOUR_REGISTRY
): Promise<TourConfig | null> {
  // Exact match first
  const exactLoader = registro[route];
  if (exactLoader) {
    try {
      const mod = await exactLoader();
      return mod.default;
    } catch {
      return null;
    }
  }

  // Prefix match for parameterized routes (e.g., /pricing-models/abc123)
  // Sort by key length descending so longer prefixes match first
  const prefixEntries = Object.entries(registro)
    .filter(([key]) => key.endsWith('/'))
    .sort(([a], [b]) => b.length - a.length);

  for (const [prefix, loader] of prefixEntries) {
    if (route.startsWith(prefix) && route.length > prefix.length) {
      try {
        const mod = await loader();
        return mod.default;
      } catch {
        return null;
      }
    }
  }

  return null;
}
